import { useMutation, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { Minus, Plus, Tags } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { type AudienceFilter, bulkTagMembers, plural } from "@/entities/audience"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"

/** «Добавить 1 человека, 2 человека», но «Добавлено: 1 человек». */
const PEOPLE_OBJECT: [string, string, string] = ["человека", "человека", "человек"]
const PEOPLE: [string, string, string] = ["человек", "человека", "человек"]

/**
 * «Группа…» над списком: добавить в группу или убрать из неё всех, кто сейчас в списке,
 * или только отмеченных галочками (picked).
 */
export function BulkGroupMenu({
	botId,
	filter,
	total,
	tags,
	picked = false,
	onDone,
}: {
	botId: string
	filter: AudienceFilter
	total: number
	tags: { id: string; name: string }[]
	picked?: boolean
	onDone?: () => void
}) {
	const who = picked ? "отмеченных" : "всех, кто в списке"
	const scope = picked ? "Отмеченные" : "Все, кто сейчас в списке"
	const queryClient = useQueryClient()
	const { confirm } = useConfirm()
	const [open, setOpen] = useState(false)
	const change = useMutation({
		mutationFn: (input: { tagId: string; action: "add" | "remove" }) =>
			bulkTagMembers(botId, input.tagId, input.action, filter),
		onSuccess: async ({ changed }, { action }) => {
			await queryClient.invalidateQueries({ queryKey: ["bot-builder", "audience", botId] })
			await queryClient.invalidateQueries({ queryKey: ["bot-builder", "stats", botId, "tags"] })
			toast.success(
				changed === 0
					? "Ничего не изменилось: все уже были так"
					: `${action === "add" ? "Добавлено" : "Убрано"}: ${changed} ${plural(changed, PEOPLE)}`,
			)
			onDone?.()
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось изменить группу")),
	})

	if (tags.length === 0) return null
	const ask = (tag: { id: string; name: string }, action: "add" | "remove") => {
		setOpen(false)
		confirm({
			title:
				action === "add"
					? `Добавить ${total} ${plural(total, PEOPLE_OBJECT)} в «${tag.name}»?`
					: `Убрать ${total} ${plural(total, PEOPLE_OBJECT)} из «${tag.name}»?`,
			message:
				action === "add"
					? `${scope} окажутся в группе. Остальные группы у них останутся.`
					: `Группа снимется у ${picked ? "отмеченных" : "всех, кто сейчас в списке"}. Остальные группы у них останутся.`,
			confirmText: action === "add" ? "Добавить" : "Убрать",
			onConfirm: async () => {
				await change.mutateAsync({ tagId: tag.id, action }).catch(() => undefined)
			},
		})
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className="h-10 shrink-0 rounded-xl"
					disabled={total === 0 || change.isPending}
					title={`Добавить в группу или убрать из неё ${who}`}
				>
					<Tags />
					<span className="hidden sm:inline">{picked ? `В группу (${total})` : "Группа"}</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-72 p-2">
				<p className="px-2 pt-1 pb-2 text-xs text-muted-foreground">
					{scope} ({total}): добавить в группу или убрать из неё
				</p>
				<ul className="max-h-80 overflow-y-auto">
					{tags.map((tag) => (
						<li
							key={tag.id}
							className="flex items-center gap-1 rounded-lg px-2 py-1 hover:bg-accent/50"
						>
							<span className="min-w-0 flex-1 truncate text-sm">{tag.name}</span>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-7 px-2 text-xs"
								onClick={() => ask(tag, "add")}
							>
								<Plus className="size-3.5" />
								Добавить
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-7 px-2 text-xs text-muted-foreground"
								onClick={() => ask(tag, "remove")}
							>
								<Minus className="size-3.5" />
								Убрать
							</Button>
						</li>
					))}
				</ul>
			</PopoverContent>
		</Popover>
	)
}
